<template>
    <div class="tournament-profile-card">
        <!-- Desktop Header -->
        <div class="header d-none d-sm-flex" v-if="participant">
            <template v-if="showDisqualificationTooltip">
                <DisqualificationTooltip :participant-id="participant.participantId" position="bottom">
                    <span :class="'participant-status ' + getStatusColor()">{{ $t(capitalizedParticipantStatus) }}<i
                            :class="getStatusIcon()"></i></span>
                </DisqualificationTooltip>
            </template>
            <template v-else-if="showRankRibbon">
                <span class="participant-status rank-ribbon" :style="rankRibbonStyle">
                    {{ $t('youAreRank', { rank }) }}
                    <i class="fa-regular fa-medal"></i>
                </span>
            </template>
            <template v-else>
                    <span :class="'participant-status ' + getStatusColor()">
                        {{ $t(capitalizedParticipantStatus) }}
                        <i :class="getStatusIcon()"></i>
                    </span>
            </template>
            <Flag class="flag" :code="countryCode" size="S"></Flag>
        </div>

        <!-- Mobile Header - Enhanced layout -->
        <div class="mobile-header d-flex d-sm-none" v-if="participant">
            <template v-if="showDisqualificationTooltip">
                <DisqualificationTooltip :participant-id="participant.participantId" position="bottom">
                    <span :class="'mobile-status ' + getStatusColor()">
                        <i :class="getStatusIcon() + ' mr-1'"></i>
                        {{ $t(capitalizedParticipantStatus) }}
                    </span>
                </DisqualificationTooltip>
            </template>
            <template v-else>
                <span :class="'mobile-status ' + getStatusColor()">
                    <i :class="getStatusIcon() + ' mr-1'"></i>
                    {{ $t(capitalizedParticipantStatus) }}
                </span>
            </template>
            <Flag class="mobile-flag" :code="countryCode" size="S"></Flag>
        </div>

        <!-- Desktop Body -->
        <div class="body d-none d-sm-block" v-if="participant">
            <v-avatar class="avatar-profile justify-center" size="103" :style="{ borderColor: partnerAccentColor }">
                <v-img :src="!isPublic ? avatar.imageURL : publicParticipant.profilePicture"></v-img>
            </v-avatar>
            <div v-if="showRankRibbon" class="status-chip-small"
                :style="{ borderColor: partnerAccentColor, color: partnerAccentColor }">
                {{ $t(capitalizedParticipantStatus) }}
                <i :class="getStatusIcon()"></i>
            </div>
            <p class="name-txt">{{ participant?.name || (isPublic ? (publicParticipant.firstName + " " +
                publicParticipant.lastName) : null) || (this.$store.state.user.firstName + " " +
                    this.$store.state.user.lastName) }}</p>
            <p class="joined-with-txt"><b>{{ $t('joinedWith') }}:</b> {{
                brokerAccount?.identifier }}<i v-if="canChangeAcc" class="fa-regular fa-shuffle ml-3 change-acc-btn"
                    :style="{ color: partnerAccentColor, borderColor: partnerAccentColor }"
                    @click="changeAccountCallback()"></i></p>
            <div class="participant-info">
                <p class="ratio"><b>{{ rankingHeader }}: {{ formattedScore }}</b></p>
                <span class="participant-info__divider"></span>
                <p class="rank">{{ rank }}° {{ $t('atRank') }}</p>
            </div>
        </div>

        <!-- Mobile Body - Compact layout -->
        <div class="mobile-body d-block d-sm-none" v-if="participant">
            <div class="mobile-user-info">
                <v-avatar class="mobile-avatar" size="64">
                    <v-img :src="!isPublic ? avatar.imageURL : publicParticipant.profilePicture"></v-img>
                </v-avatar>
                <div class="mobile-user-details">
                    <p class="mobile-name">{{ participant?.name || (isPublic ? (publicParticipant.firstName + " " +
                        publicParticipant.lastName) : null) || (this.$store.state.user.firstName + " " +
                            this.$store.state.user.lastName) }}</p>
                    <p class="mobile-broker"><i class="fa-regular fa-wallet mr-1"></i>{{ brokerAccount?.identifier }}
                        <i v-if="canChangeAcc" class="fa-regular fa-shuffle ml-2 mobile-change-btn"
                            @click="changeAccountCallback()"></i>
                    </p>
                </div>
            </div>

            <div class="mobile-stats">
                <div class="mobile-stat-item">
                    <span class="stat-label">{{ rankingHeader }}</span>
                    <span class="stat-value">{{ formattedScore }}</span>
                </div>
                <div class="mobile-stat-item">
                    <span class="stat-label">{{ $t('rank') }}</span>
                    <span class="stat-value">{{ rank }}°</span>
                </div>
            </div>
        </div>

        <!-- Bracket Progress (Desktop) -->
        <BracketProgress
            v-if="showBracketProgress && bracketTournamentId"
            class="d-none d-sm-block"
            :score-bracket="participant.scoreBracket"
            :tournament-id="bracketTournamentId"
        />

        <!-- Desktop Footer -->
        <div class="footer d-none d-sm-flex" v-if="participant">
            <button v-if="!isPublic" class="share-btn"
                :style="{ color: partnerAccentColor, borderColor: partnerAccentColor }" @click="toggleShareLink"><i
                    class="fa-regular fa-share-nodes mr-3"></i>{{
                        $t('shareMyProgress') }}</button>
        </div>

        <!-- Bracket Progress (Mobile) -->
        <BracketProgress
            v-if="showBracketProgress && bracketTournamentId"
            class="d-block d-sm-none"
            :score-bracket="participant.scoreBracket"
            :tournament-id="bracketTournamentId"
        />

        <!-- Mobile Footer - Compact layout -->
        <div class="mobile-footer d-flex d-sm-none" v-if="participant && !isPublic">
            <button class="mobile-share-btn" @click="toggleShareLink">
                <i class="fa-regular fa-share-nodes mr-2"></i>{{ $t('share') }}
            </button>
        </div>

        <!-- <p class="not-enrolled-txt" v-if="!participant">{{ $t('notParticipating') }}</p> -->
        <ShareLink :tournamentParticipant="participant" v-if="showShareLink" :fundingChallengeTestId="1"
            @closeWindow="hideShareModal" />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import ShareLink from "@/views/Funding/Simulation/components/ShareLinkTest.vue";
import DisqualificationTooltip from "@/components/DisqualificationTooltip.vue";
import BracketProgress from "@/components/BracketProgress.vue";

export default defineComponent({
    name: 'TournamentProfileCard',
    components: {
        ShareLink,
        DisqualificationTooltip,
        BracketProgress,
    },
    props: {
        tournament: {
            type: Object,
            required: false
        },
        participant: {
            type: Object,
            required: true,
            default: { status: '' }
        },
        countryCode: {
            type: String,
            required: true,
            default: 'CO'
        },
        brokerAccount: {
            type: Object,
            required: true,
            default: { identifier: 'Swiset Wallet' }
        },
        swisetRatio: {
            type: Number,
            required: false,
            default: 0
        },
        rank: {
            type: Number || String,
            required: true,
            default: 100
        },
        shareBtnCallback: {
            type: Function,
            required: false,
        },
        changeAccountCallback: {
            type: Function,
            required: false
        },
        canChangeAcc: {
            type: Boolean,
            required: false,
            default: false
        },
        rankingType: {
            type: String,
            required: false,
        },
        tournamentClosed: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        showDisqualificationTooltip() {
            return this.participant?.status === 'DISQUALIFIED';
        },
        /** Partner accent: tournament primary → club primary → swiset green (same chain as the leaderboard header). */
        partnerAccentColor() {
            const colors = this.$store.state.tournament?.tournamentSettings?.colors;
            const raw = colors?.primary || this.$store.state.clubMember?.club?.primaryColor;
            if (raw) return `#${String(raw).replace('#', '')}`;
            return '#009F63';
        },
        /** Figma redesign: while actively competing the ribbon shows "You are N°" in the partner color. */
        showRankRibbon() {
            const statusText = this.getParticipantStatus().text;
            return (statusText === 'participating' || statusText === 'winning') && this.rank && this.rank !== '---';
        },
        rankRibbonStyle() {
            const clean = this.partnerAccentColor.replace('#', '');
            return {
                backgroundColor: this.partnerAccentColor,
                color: this.getContrastYIQ(clean) === 'black' ? '#14161b' : '#fff',
            };
        },
        capitalizedParticipantStatus() {
            return this.getParticipantStatus().text;
        },
        rankingHeader() {
            // Get the ranking type from props or fallback to store
            const rankingType = this.rankingType || this.$store.state.tournament?.tournamentSettings.tournamentRankingType;

            // Return the appropriate header based on the ranking type - same logic as Table.vue
            switch (rankingType) {
                case 'SWISET_RATIO':
                    return this.$t('swisetRatio');
                case 'BALANCE':
                    return this.$t('balance');
                case 'EQUITY_RT':
                    return this.$t('equity');
                case 'PROFIT_PERCENTAGE':
                    return this.$t('profit');
                case 'GROSS_LOSS':
                    return this.$t('grossLoss');
                case 'PERFORMANCE_RATIO':
                    return this.$t('performanceRatio');
                default:
                    return this.$t('profit');
            }
        },
        showBracketProgress() {
            const features = this.$store.state.tournament?.selectedFeatures;
            return features && Array.isArray(features) && features.includes('TOURNAMENT_RANKING_AGG_VOLUME')
                && this.participant?.scoreBracket != null;
        },
        bracketTournamentId() {
            return this.$store.state.tournament?.tournamentSettings?.tournament?.id || null;
        },
        formattedScore() {
            const value = this.swisetRatio;
            if (value === null || value === undefined) return '-';

            // Get the ranking type from props or fallback to store
            const rankingType = this.rankingType || this.$store.state.tournament?.tournamentSettings.tournamentRankingType || 'SWISET_RATIO';

            // Format the value based on the ranking type - similar to formatRankingValue in Table.vue
            switch (rankingType) {
                case 'BALANCE':
                    // Format with thousands separator
                    return new Intl.NumberFormat(this.$i18n.locale).format(value.toFixed(2));

                case 'PROFIT_PERCENTAGE':
                case 'PERFORMANCE_RATIO':
                    // Format as percentage
                    return `${value.toFixed(2)}%`;

                case 'SWISET_RATIO':
                case 'EQUITY_RT':
                case 'GROSS_LOSS':
                default:
                    // Format as plain number
                    return value.toFixed(2);
            }
        }
    },
    data() {
        return {
            avatar: {
                imageURL: this.$store.state.user.imageUrl
            },
            showShareLink: false,
            publicParticipant: {},
            isPublic: false,
        }
    },
    created() {
        this.setParticipantEmbedRank();
    },
    methods: {
        getContrastYIQ(hexcolor) {
            const hex = String(hexcolor).replace('#', '');
            if (!/^[0-9a-fA-F]{6}$/.test(hex)) return 'white';
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            return (yiq >= 128) ? 'black' : 'white';
        },
        toggleShareLink() {
            this.showShareLink = !this.showShareLink;
        },
        showShareModal() {
            this.showShareLink = true;
        },
        hideShareModal() {
            this.showShareLink = false;
        },
        getParticipantStatus() {
            const status = this.participant?.status?.toLowerCase() || '';
            // Pre-start: "Winning"/"Participating" imply a live competition, so
            // active participants get a neutral "Enrolled" tag until it starts
            // (SFB-1198). Live-state tagging below is untouched once running.
            const startTime = this.$store.state.tournament?.startTime;
            const notStarted = startTime && new Date() < new Date(startTime);
            if (notStarted && (status === 'active' || status === 'participating')) {
                return { text: 'enrolled', color: 'active', icon: 'fa-regular fa-circle-check' };
            }
            if (status === 'winner' || (status === 'active' && this.rank === 1 && (new Date() > new Date(this.$store.state.tournament.endTime)))) {
                return { text: 'winner', color: 'winning', icon: 'fa-regular fa-medal' };
            }
            if (status === 'active' && this.rank > 0 && this.rank <= 1) {
                return { text: 'winning', color: 'winning', icon: 'fa-regular fa-medal' };
            }
            if (this.tournamentClosed && (status === 'active' || status === 'participating')) {
                return { text: 'tournamentParticipationFinished', color: 'active', icon: 'fa-regular fa-flag-checkered' };
            }
            if (status === 'active' || status === 'participating') {
                return { text: 'participating', color: 'active', icon: 'fa-regular fa-gamepad' };
            }
            if (status === 'paused') {
                return { text: 'notConsistent', color: 'paused', icon: 'fa-regular fa-triangle-exclamation' };
            }
            if (status === 'disqualified') {
                return { text: 'disqualified', color: 'disqualified', icon: 'fa-regular fa-circle-exclamation' };
            }
            return { text: status.charAt(0).toUpperCase() + status.slice(1), color: 'no-enrolled', icon: '' };
        },
        setParticipantEmbedRank() {
            if (this.$route.query.identifier) {
                let acc = this.$route.query.identifier;
                let participant = this.$store.state.tournament.participants.find(p => p.brokerAccountIdentifier === acc);
                this.publicParticipant = participant;
                this.isPublic = true;
            }
        },
        getStatusColor() {
            return this.getParticipantStatus().color;
        },
        getStatusIcon() {
            return this.getParticipantStatus().icon;
        }
    },
});
</script>

<style lang="scss" scoped>
$swt-success: var(--v-mainGreen-base);
$swt-error: #C42A14;
$swt-yellow: #FFF75F;
$swt-warning: #FF8933;

.tournament-profile-card {
    border: 1px solid #EBEBEB;
    border-radius: 15px;
    margin-left: auto;
    background-color: var(--v-fullWhite-base);
    font-family: 'Avenir', sans-serif;
    text-align: center;
    position: relative;
    overflow: hidden;

    .header {
        display: flex;
        justify-content: space-between;
        padding-top: 25px;
        align-items: center;
        margin-bottom: 10px;

        .participant-status {
            border-top-right-radius: 20px;
            border-bottom-right-radius: 20px;
            background-color: var(--v-mainBlack-base);
            color: #fff;
            padding: 8px 20px;

            &.rank-ribbon {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                font-size: 16px;
            }

            &.active {
                background-color: #9a9a9a;
            }

            &.winning {
                background-color: $swt-success;
            }

            &.paused {
                background-color: $swt-warning;
            }

            &.disqualified {
                background-color: $swt-error;
            }

            &.no-enrolled {
                background-color: var(--v-black_lightGrey-base);
            }

            i {
                margin-left: 10px;
            }
        }

        .flag {
            margin-right: 24px;
            height: 20px;
            width: 25px;
        }
    }

    .body {
        .avatar-profile {
            border: 2px solid #009F63; /* overridden inline with the partner accent */
            margin-bottom: 5px;
        }

        .status-chip-small {
            /* flex (block-level) + fit-content keeps the chip on its own line below the avatar */
            display: flex;
            width: fit-content;
            align-items: center;
            gap: 5px;
            height: 28px;
            padding: 3px 10px;
            border: 1px solid;
            border-radius: 30px;
            font-size: 14px;
            font-weight: 800;
            margin: 0 auto 4px;

            i {
                font-size: 13px;
            }
        }

        .name-txt {
            font-family: 'Aeonik', 'Avenir', sans-serif;
            font-size: 26px;
            font-weight: 700;
            line-height: 1.3;
            margin: 6px 16px 5px;
        }

        .joined-with-txt {
            font-size: 14px;
            margin-bottom: 0;

            b {
                font-weight: 800;
            }
        }

        .participant-info {
            display: flex;
            align-items: center;
            justify-content: space-around;
            margin-bottom: 10px;
            border: 1px solid #9A9A9A;
            border-radius: 10px;
            margin: 12px 24px;
            padding: 8px 10px;

            .ratio {
                font-size: 14px;
                font-weight: 600;
                margin-bottom: 0;
            }

            &__divider {
                width: 1px;
                align-self: stretch;
                background-color: #9A9A9A;
            }

            .rank {
                font-size: 14px;
                font-weight: 600;
                margin-bottom: 0;
            }
        }

        .change-acc-btn {
            font-size: 16px;
            color: var(--v-mainGreen-base);
            cursor: pointer;
            border-radius: 50%;
            border: 1px solid var(--v-mainGreen-base);
            padding: 5px;
        }
    }

    .footer {
        padding-bottom: 25px;

        .share-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 40px;
            width: calc(100% - 48px);
            margin: 0 24px;
            color: var(--v-mainGreen-base);
            border: 1px solid var(--v-mainGreen-base);
            border-radius: 30px;
            padding: 8px 20px;
            font-size: 16px;
            cursor: pointer;
            transition: opacity 0.2s ease;

            &:hover {
                opacity: 0.75;
            }
        }
    }

    .not-enrolled-txt {
        font-size: 22px;
        font-weight: bold;
        color: var(--v-mainBlack-base);
        padding: 20px;
    }

    /* Mobile-specific styles */
    .mobile-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 15px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        .mobile-status {
            font-size: 12px;
            font-weight: 500;
            padding: 4px 10px;
            border-radius: 12px;
            display: inline-flex;
            align-items: center;

            &.active {
                background-color: rgba(154, 154, 154, 0.2);
                color: #666;
            }

            &.winning {
                background-color: rgba($swt-success, 0.15);
                color: $swt-success;
            }

            &.paused {
                background-color: rgba($swt-warning, 0.15);
                color: $swt-warning;
            }

            &.disqualified {
                background-color: rgba($swt-error, 0.15);
                color: $swt-error;
            }

            &.no-enrolled {
                background-color: rgba(154, 154, 154, 0.2);
                color: #666;
            }

            i {
                font-size: 11px;
            }
        }

        .mobile-flag {
            height: 16px;
            width: 20px;
        }
    }

    .mobile-body {
        padding: 12px 15px;

        .mobile-user-info {
            display: flex;
            align-items: center;
            text-align: left;
            margin-bottom: 12px;

            .mobile-avatar {
                border: 1px solid #ddd;
                margin-right: 12px;
            }

            .mobile-user-details {
                flex: 1;

                .mobile-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin: 0 0 4px;
                    line-height: 1.3;
                }

                .mobile-broker {
                    font-size: 12px;
                    color: #666;
                    margin: 0;
                    line-height: 1.3;

                    .mobile-change-btn {
                        color: var(--v-mainGreen-base);
                        cursor: pointer;
                        border-radius: 50%;
                        border: 1px solid var(--v-mainGreen-base);
                        padding: 2px;
                        font-size: 12px;
                    }
                }
            }
        }

        .mobile-stats {
            display: flex;
            justify-content: space-between;
            margin: 0 -5px;

            .mobile-stat-item {
                flex: 1;
                background-color: rgba(0, 0, 0, 0.02);
                border-radius: 8px;
                padding: 6px 8px;
                margin: 0 5px;
                display: flex;
                flex-direction: column;

                .stat-label {
                    font-size: 11px;
                    color: #666;
                    margin-bottom: 2px;
                }

                .stat-value {
                    font-size: 16px;
                    font-weight: bold;
                    color: var(--v-mainBlack-base);
                }
            }
        }
    }

    .mobile-footer {
        padding: 0 15px 12px;
        justify-content: center;

        .mobile-share-btn {
            background-color: transparent;
            border: 1px solid var(--v-mainGreen-base);
            color: var(--v-mainGreen-base);
            border-radius: 20px;
            padding: 6px 16px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;

            &:active {
                background-color: rgba(var(--v-mainGreen-base), 0.1);
            }
        }
    }
}

// Additional responsive adjustments
@media (max-width: 600px) {
    .tournament-profile-card {
        border-radius: 12px;
        margin-bottom: 16px;

        .not-enrolled-txt {
            font-size: 16px;
            padding: 16px;
        }
    }
}
</style>

